/**
 * Central configuration for subscription plans, limits, and feature flags.
 * Single source of truth — change limits here and they propagate everywhere.
 */

export const FREE_PLAN_LIMITS = {
  dreams: 2,
  goalsPerDream: Infinity,
  milestonesPerGoal: Infinity,
  categories: 3,
} as const;

export const FREE_DEFAULT_CATEGORIES = ["Personal", "Family", "Professional"] as const;

export const PREMIUM_FEATURES = [
  {
    icon: "star",
    title: "Unlimited dreams",
    description: "Create as many dreams as you can imagine",
  },
  {
    icon: "target",
    title: "Unlimited goals & milestones",
    description: "Break every dream into as many steps as you need",
  },
  {
    icon: "sparkles",
    title: "AI-powered planning",
    description: "Let AI generate a personalised milestone roadmap",
  },
  {
    icon: "calendar",
    title: "Advanced scheduler",
    description: "Full planner access — week, month, and year views",
  },
  {
    icon: "tag",
    title: "Unlimited categories",
    description: "Organise dreams your way without limits",
  },
] as const;

export type UpgradeTrigger =
  | "dream_limit"
  | "goal_limit"
  | "milestone_limit"
  | "ai_plan"
  | "planner_view"
  | "category_limit"
  | "reorder"
  | "duplicate"
  | "finance"
  | "banner";

export const UPGRADE_COPY: Record<UpgradeTrigger, { headline: string; subtext: string }> = {
  dream_limit: {
    headline: "You've outgrown the free plan",
    subtext: "Free accounts can track up to 2 dreams. Upgrade to unlock unlimited dreams and start planning everything you want to achieve.",
  },
  goal_limit: {
    headline: "Ready to go deeper?",
    subtext: "Free accounts include 2 goals per dream. Upgrade to add unlimited goals and structure your path in detail.",
  },
  milestone_limit: {
    headline: "Keep the momentum going",
    subtext: "Free accounts include 5 milestones per goal. Upgrade to track every step without limits.",
  },
  ai_plan: {
    headline: "Unlock AI-powered planning",
    subtext: "Premium members get a personalised AI roadmap built around their dream, goals, and timeline.",
  },
  planner_view: {
    headline: "Unlock month and year views",
    subtext: "Free accounts have access to the week view. Upgrade to Premium to plan across months and years.",
  },
  category_limit: {
    headline: "Need more categories?",
    subtext: "Upgrade to Premium to organise your dreams with unlimited custom categories.",
  },
  reorder: {
    headline: "Reorder goals & milestones",
    subtext: "Premium members can drag and reorder goals and milestones to match their personal priority.",
  },
  duplicate: {
    headline: "Duplicate goals & milestones",
    subtext: "Premium members can duplicate goals and milestones to reuse your plans in seconds.",
  },
  finance: {
    headline: "Track your dream's finances",
    subtext: "Premium members can log expenses and income on any milestone and see a consolidated finance report for the whole dream.",
  },
  banner: {
    headline: "Unlock your full potential",
    subtext: "Upgrade to Premium for unlimited dreams, AI planning, and advanced scheduling.",
  },
};
